/**
 * Document Type Utilities
 * Provides utilities for working with document types in the application
 */

import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { getBrandLocale } from './brandCurrency';

const exportT = (key, opts) =>
  i18n.t(`modules.comptabilite.pages.accountingExportUtils.${key}`, opts);

function paymentLineDescription(type, ref) {
  const key = ref ? `${type}_ref` : type;
  return exportT(`payment_line_${key}`, ref ? { ref } : undefined);
}

/**
 * Hook that returns a function to translate document types
 * @returns {Function} A function that translates document types
 */
export const useDocumentTypeTranslator = () => {
  const { t } = useTranslation();

  /**
   * Translate a document type code to a human-readable name
   * @param {string} type The document type code (e.g., "purchase_order", "shipping")
   * @returns {string} The translated document type name
   */
  const translateDocumentType = (type) => {
    // Array of type variants to try
    const typeVariants = [
      type, // Original type
      type.replace('-', '_').toLowerCase(), // Replace hyphens with underscores, lowercase
      type.replace('_', '-').toLowerCase(), // Replace underscores with hyphens, lowercase  
      type.toLowerCase(), // Just lowercase
      type.replace(/\s+/g, '_').toLowerCase(), // Replace spaces with underscores, lowercase
      type.replace(/\s+/g, '-').toLowerCase(), // Replace spaces with hyphens, lowercase
      type.charAt(0).toUpperCase() + type.slice(1), // Capitalize first letter
      type.replace(/_/g, ' ').toLowerCase(), // Replace underscores with spaces, lowercase
      type.replace(/-/g, ' ').toLowerCase() // Replace hyphens with spaces, lowercase
    ];
    
    // Try each variant in document_types translations first
    for (const variant of typeVariants) {
      const specificTranslation = t(`document_types.${variant}`, '');
      if (specificTranslation && specificTranslation !== '') {
        return specificTranslation;
      }
    }

    // Try dashboard translations
    for (const variant of typeVariants) {
      const dashboardTranslation = t(`dashboard.${variant}`, '');
      if (dashboardTranslation && dashboardTranslation !== '') {
        return dashboardTranslation;
      }
    }

    // Try document creation translations
    for (const variant of typeVariants) {
      const creationTranslation = t(`document_creation.${variant}`, '');
      if (creationTranslation && creationTranslation !== '') {
        return creationTranslation;
      }
    }

    // Fallback to capitalized type if no translation found
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/[_-]/g, ' ');
  };

  return translateDocumentType;
};

export function resolvePaymentDocumentDisplayType(doc) {
  if (!doc || doc.type == null || doc.type === '') return doc?.type;
  const raw = String(doc.type);
  const typeUnderscore = raw.replace(/-/g, '_');
  const num = String(doc.number ?? '').trim();
  const title = String(doc.title ?? '').toLowerCase();
  const prefixMatch = num.match(/^([A-Za-z]+)/);
  const prefix = prefixMatch ? prefixMatch[1].toUpperCase() : '';

  if ((typeUnderscore === 'payment_voucher' || typeUnderscore === 'payment_slip') &&
      (prefix === 'RP' || prefix === 'REC')) {
    return 'payment_receipt';
  }
  if (typeUnderscore === 'payment_receipt' && prefix === 'BV') {
    return 'payment_voucher';
  }
  if ((typeUnderscore === 'payment_voucher' || typeUnderscore === 'payment_slip') &&
      (title.includes('reçu de paiement') || title.includes('recu de paiement'))) {
    return 'payment_receipt';
  }
  if (typeUnderscore === 'payment_receipt' &&
      title.includes('bon de versement') &&
      !title.includes('reçu') &&
      !title.includes('recu')) {
    return 'payment_voucher';
  }
  return raw;
}

export function isSupplierCreditNoteDocument(doc) {
  if (!doc) return false;
  const kn = String(doc.type || '').replace(/-/g, '_');
  if (kn !== 'credit_note') return false;
  if (
    doc.creditNoteParty === 'client' ||
    doc.credit_note_party === 'client' ||
    doc.isSupplier === false ||
    doc.is_supplier === false
  ) {
    return false;
  }
  if (
    doc.isSupplier ||
    doc.is_supplier ||
    doc.creditNoteParty === 'supplier' ||
    doc.credit_note_party === 'supplier'
  ) {
    return true;
  }
  const num = String(doc.number ?? '').trim().toUpperCase();
  if (/^AVO[-_]/.test(num)) return true;
  return false;
}

export function isClientCreditNoteDocument(doc) {
  if (!doc) return false;
  const kn = String(doc.type || '').replace(/-/g, '_');
  if (kn !== 'credit_note') return false;
  return !isSupplierCreditNoteDocument(doc);
}

export function getCreditNoteLabelSupplier() {
  return i18n.t('facturation_sections.create.credit_note_supplier');
}

export function getCreditNoteLabelClient() {
  return i18n.t('facturation_sections.create.credit_note_client');
}

/** @deprecated Prefer getCreditNoteLabelSupplier() for locale-aware UI labels */
export const CREDIT_NOTE_LABEL_SUPPLIER = getCreditNoteLabelSupplier();
/** @deprecated Prefer getCreditNoteLabelClient() for locale-aware UI labels */
export const CREDIT_NOTE_LABEL_CLIENT = getCreditNoteLabelClient();

/** Motifs Bon de versement (décaissement fournisseur — pas Reçu de paiement client). */
const PAYMENT_VOUCHER_REASON_KEYS = [
  'facture_fournisseur',
  'bon_commande',
  'acompte',
  'solde',
  'avance',
  'regularisation',
  'remboursement',
];

const LEGACY_PAYMENT_VOUCHER_REASON_LABELS = {
  facture: 'facture_fournisseur',
  'paiement facture': 'facture_fournisseur',
  'paiement facture fournisseur': 'facture_fournisseur',
  'paiement bon de commande': 'bon_commande',
  'acompte fournisseur': 'acompte',
  'solde commande': 'solde',
  'avance fournisseur': 'avance',
  régularisation: 'regularisation',
  regularisation: 'regularisation',
  'remboursement (avoir)': 'remboursement',
};

export function getPaymentVoucherReasonOptions() {
  return PAYMENT_VOUCHER_REASON_KEYS.map((value) => ({
    value,
    label: i18n.t(`common.payment_voucher_reasons.${value}`),
  }));
}

/** @deprecated Prefer getPaymentVoucherReasonOptions() for locale-aware UI labels */
export const PAYMENT_VOUCHER_REASON_OPTIONS = getPaymentVoucherReasonOptions();

export function formatPaymentVoucherReason(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const resolved = resolvePaymentVoucherReasonValue(s);
  if (PAYMENT_VOUCHER_REASON_KEYS.includes(resolved)) {
    return i18n.t(`common.payment_voucher_reasons.${resolved}`);
  }
  return LEGACY_PAYMENT_VOUCHER_REASON_LABELS[s.toLowerCase()]
    ? i18n.t(`common.payment_voucher_reasons.${LEGACY_PAYMENT_VOUCHER_REASON_LABELS[s.toLowerCase()]}`)
    : s;
}

/** Restaure la valeur select à partir du slug ou d’un libellé enregistré en BDD. */
export function resolvePaymentVoucherReasonValue(motifOrSlug) {
  const s = String(motifOrSlug ?? '').trim();
  if (!s) return '';
  const byValue = PAYMENT_VOUCHER_REASON_KEYS.find((key) => key === s);
  if (byValue) return byValue;
  const lower = s.toLowerCase();
  const byLabel = getPaymentVoucherReasonOptions().find(
    (o) => o.label.toLowerCase() === lower,
  );
  if (byLabel) return byLabel.value;
  const legacyKey = LEGACY_PAYMENT_VOUCHER_REASON_LABELS[lower];
  if (legacyKey) return legacyKey;
  return s;
}

/** Libellés des lignes BV / RP (alignés sur le tableau « Description des opérations »). */
export function buildPaymentDocumentLineDescriptions(paymentLines) {
  const arr = Array.isArray(paymentLines) ? paymentLines.filter(Boolean) : [];
  return arr
    .map((row) => {
      const desc = String(row.description || '').trim();
      if (desc) return desc;
      const ref = String(row.reference || '').trim();
      if (!ref) return '';
      if (row.lineType === 'credit_note') {
        const descLower = desc.toLowerCase();
        if (descLower.includes('vente')) return desc;
        return paymentLineDescription('credit_note_supplier', ref);
      }
      if (row.lineType === 'invoice') {
        return paymentLineDescription('invoice_collection', ref);
      }
      return paymentLineDescription('purchase_order', ref);
    })
    .filter(Boolean);
}

/**
 * Titre métier BV / RP : une ligne → libellé exact ; plusieurs → concaténation compacte.
 * Sans préfixe « Versement — » (réservé aux titres archive).
 */
export function summarizePaymentDocumentTitleCore(paymentLines, maxLen = 120) {
  const descriptions = buildPaymentDocumentLineDescriptions(paymentLines);
  if (descriptions.length === 0) return '';
  if (descriptions.length === 1) {
    return descriptions[0].length <= maxLen
      ? descriptions[0]
      : `${descriptions[0].slice(0, Math.max(0, maxLen - 1))}…`;
  }
  if (descriptions.length === 2) {
    const joined = `${descriptions[0]} · ${descriptions[1]}`;
    return joined.length <= maxLen ? joined : `${joined.slice(0, Math.max(0, maxLen - 1))}…`;
  }
  const joined = descriptions.slice(0, 3).join(' · ');
  if (descriptions.length > 3) {
    const extra = descriptions.length - 3;
    const suffix = ` ${exportT('payment_line_more_suffix', { extra })}`;
    const room = Math.max(0, maxLen - suffix.length);
    return `${joined.slice(0, room)}${suffix}`;
  }
  return joined.length <= maxLen ? joined : `${joined.slice(0, Math.max(0, maxLen - 1))}…`;
}

export function canonicalArchiveDocTypeKey(type) {
  return String(type ?? '')
    .trim()
    .replace(/-/g, '_')
    .toLowerCase();
}

export function findArchiveDocumentByNumber(documents, number) {
  const ref = String(number ?? '').trim();
  if (!ref || !Array.isArray(documents)) return null;
  return documents.find((d) => String(d?.number ?? '').trim() === ref) || null;
}

function archiveDocTotalAmount(doc) {
  if (!doc) return 0;
  let total =
    Number(doc.grandTotal || doc.grand_total || doc.total || doc.amount || doc.paymentAmount || doc.payment_amount) ||
    0;
  if (!total && Number(doc.creditNoteAmount || doc.credit_note_amount) > 0) {
    total = Number(doc.creditNoteAmount || doc.credit_note_amount);
  }
  if (!total && Array.isArray(doc.items) && doc.items.length > 0) {
    total = doc.items.reduce(
      (sum, item) =>
        sum +
        (Number(item.total) ||
          Number(item.quantity) * Number(item.price || item.unitPrice) ||
          0),
      0,
    );
  }
  return Math.round(total * 100) / 100;
}

function archiveDocSupplierFields(doc) {
  if (!doc) return { supplierName: '', supplierId: '' };
  const supplierName = String(
    doc.supplierName ||
      doc.supplier_name ||
      doc.supplier ||
      doc.client ||
      '',
  ).trim();
  const supplierId = String(doc.supplierId || doc.clientId || doc.supplier_id || doc.client_id || '').trim();
  return { supplierName, supplierId };
}

/** Bon de versement : préremplissage depuis un BC archivé. */
export function buildPaymentVoucherPatchFromPurchaseOrder(doc) {
  if (!doc || canonicalArchiveDocTypeKey(doc.type) !== 'purchase_order') return {};
  const bcRef = String(doc.number || '').trim();
  const total = archiveDocTotalAmount(doc);
  const { supplierName, supplierId } = archiveDocSupplierFields(doc);
  const desc = paymentLineDescription('purchase_order', bcRef);
  return {
    purchaseOrderRef: bcRef,
    supplierName,
    supplier: supplierId,
    paymentLines: [{
      lineType: 'purchase_order',
      reference: bcRef,
      description: desc,
      amount: total || 0,
      relatedInvoice: '',
    }],
  };
}

/** Ligne vide pour Détails du paiement (Bon de versement). */
export function emptyPaymentVoucherLine(lineType = 'purchase_order') {
  return {
    lineType: lineType === 'credit_note' ? 'credit_note' : 'purchase_order',
    reference: '',
    description: '',
    amount: 0,
    relatedInvoice: '',
  };
}

/** Ligne BC typée depuis un document archivé. */
export function buildPaymentVoucherLineFromPurchaseOrder(doc) {
  if (!doc) return emptyPaymentVoucherLine('purchase_order');
  const patch = buildPaymentVoucherPatchFromPurchaseOrder(doc);
  const line = patch.paymentLines?.[0] || {};
  return {
    lineType: 'purchase_order',
    reference: patch.purchaseOrderRef || '',
    description: line.description || '',
    amount: Number(line.amount) || 0,
    relatedInvoice: '',
  };
}

/** Ligne Avoir sur achat typée depuis un document archivé. */
export function buildPaymentVoucherLineFromCreditNote(doc) {
  if (!doc) return emptyPaymentVoucherLine('credit_note');
  const patch = buildPaymentVoucherPatchFromCreditNote(doc);
  const line = patch.paymentLines?.[0] || {};
  return {
    lineType: 'credit_note',
    reference: patch.supplierCreditNoteRef || '',
    description: line.description || '',
    amount: Number(line.amount) || 0,
    relatedInvoice: patch.relatedInvoices || '',
  };
}

/** Agrège les refs d'en-tête depuis les lignes de paiement BV. */
export function syncPaymentVoucherHeaderFromLines(lines) {
  const arr = Array.isArray(lines) ? lines : [];
  const bcRefs = [];
  const cnRefs = [];
  const invRefs = [];

  arr.forEach((line) => {
    if (!line) return;
    const ref = String(line.reference || '').trim();
    if (!ref) return;
    if (line.lineType === 'credit_note') {
      cnRefs.push(ref);
      const inv = String(line.relatedInvoice || '').trim();
      if (inv) invRefs.push(inv);
    } else {
      bcRefs.push(ref);
    }
  });

  const unique = (list) => [...new Set(list.filter(Boolean))];
  const purchaseOrderRef = unique(bcRefs).join(', ');
  const supplierCreditNoteRef = unique(cnRefs).join(', ');
  const relatedInvoices = unique(invRefs).join(', ');

  return { purchaseOrderRef, supplierCreditNoteRef, relatedInvoices };
}

/** Normalise les lignes chargées (anciens BV sans lineType). */
export function normalizePaymentVoucherLines(rows, headerRefs = {}) {
  const arr = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (!arr.length) {
    const po = String(headerRefs.purchaseOrderRef || '').trim();
    const cn = String(headerRefs.supplierCreditNoteRef || '').trim();
    if (po) {
      return [{
        lineType: 'purchase_order',
        reference: po,
        description: paymentLineDescription('purchase_order', po),
        amount: 0,
        relatedInvoice: '',
      }];
    }
    if (cn) {
      return [{
        lineType: 'credit_note',
        reference: cn,
        description: paymentLineDescription('credit_note_supplier', cn),
        amount: 0,
        relatedInvoice: String(headerRefs.relatedInvoices || '').trim(),
      }];
    }
    return [emptyPaymentVoucherLine('purchase_order')];
  }

  return arr.map((row) => {
    if (row.lineType === 'purchase_order' || row.lineType === 'credit_note') {
      return {
        lineType: row.lineType,
        reference: String(row.reference || '').trim(),
        description: String(row.description || '').trim(),
        amount: Number(row.amount) || 0,
        relatedInvoice: String(row.relatedInvoice || '').trim(),
      };
    }
    const desc = String(row.description || '').trim();
    const lower = desc.toLowerCase();
    if (lower.includes('avoir')) {
      return {
        lineType: 'credit_note',
        reference: String(headerRefs.supplierCreditNoteRef || '').trim(),
        description: desc,
        amount: Number(row.amount) || 0,
        relatedInvoice: String(headerRefs.relatedInvoices || '').trim(),
      };
    }
    if (lower.includes('bon de commande') || lower.includes('commande')) {
      return {
        lineType: 'purchase_order',
        reference: String(headerRefs.purchaseOrderRef || '').trim(),
        description: desc,
        amount: Number(row.amount) || 0,
        relatedInvoice: '',
      };
    }
    return {
      ...emptyPaymentVoucherLine('purchase_order'),
      description: desc,
      amount: Number(row.amount) || 0,
    };
  });
}

/** Refs déjà utilisées sur d'autres lignes BV (même type). */
export function getUsedPaymentVoucherLineRefs(lines, excludeIndex, lineType) {
  const type = lineType === 'credit_note' ? 'credit_note' : 'purchase_order';
  return new Set(
    (Array.isArray(lines) ? lines : [])
      .map((line, i) => {
        if (i === excludeIndex) return '';
        const lt = line?.lineType === 'credit_note' ? 'credit_note' : 'purchase_order';
        if (lt !== type) return '';
        return String(line?.reference || '').trim();
      })
      .filter(Boolean),
  );
}

/** Bon de versement : préremplissage depuis un Avoir sur achat archivé. */
export function buildPaymentVoucherPatchFromCreditNote(doc) {
  if (!doc || canonicalArchiveDocTypeKey(doc.type) !== 'credit_note') return {};
  if (!isSupplierCreditNoteDocument(doc)) return {};
  const cnRef = String(doc.number || '').trim();
  const invRef = String(
    doc.originalInvoiceNumber ||
      doc.original_invoice_number ||
      (doc.originalInvoice && doc.originalInvoice.number) ||
      '',
  ).trim();
  const bcRef = String(
    doc.returnOriginalPurchaseOrder ||
      doc.return_original_purchase_order ||
      doc.purchaseOrderReference ||
      doc.purchase_order_reference ||
      '',
  ).trim();
  const total = archiveDocTotalAmount(doc);
  const { supplierName, supplierId } = archiveDocSupplierFields(doc);
  const desc = paymentLineDescription('credit_note_supplier', cnRef);
  return {
    supplierCreditNoteRef: cnRef,
    relatedInvoices: invRef,
    purchaseOrderRef: bcRef,
    supplierName,
    supplier: supplierId,
    paymentLines: [{
      lineType: 'credit_note',
      reference: cnRef,
      description: desc,
      amount: total || 0,
      relatedInvoice: invRef,
    }],
  };
}

/** Bon de versement : préremplissage depuis une réf. facture fournisseur (BRF / avoir). */
export function buildPaymentVoucherPatchFromSupplierInvoice(documents, invoiceRef) {
  const ref = String(invoiceRef ?? '').trim();
  if (!ref || !Array.isArray(documents)) {
    return { relatedInvoices: ref, paymentReason: ref ? 'facture_fournisseur' : '' };
  }

  const cn = documents.find((d) => {
    if (canonicalArchiveDocTypeKey(d.type) !== 'credit_note') return false;
    if (!isSupplierCreditNoteDocument(d)) return false;
    const inv = String(d.originalInvoiceNumber || d.original_invoice_number || '').trim();
    return inv === ref;
  });
  if (cn) {
    return {
      ...buildPaymentVoucherPatchFromCreditNote(cn),
      relatedInvoices: ref,
      paymentReason: 'facture_fournisseur',
    };
  }

  const brf = documents.find((d) => {
    if (canonicalArchiveDocTypeKey(d.type) !== 'return_slip') return false;
    const inv = String(
      d.returnOriginalInvoice || d.return_original_invoice || '',
    ).trim();
    return inv === ref;
  });
  if (brf) {
    const bcRef = String(
      brf.returnOriginalPurchaseOrder || brf.return_original_purchase_order || '',
    ).trim();
    const { supplierName, supplierId } = archiveDocSupplierFields(brf);
    return {
      relatedInvoices: ref,
      purchaseOrderRef: bcRef,
      paymentReason: 'facture_fournisseur',
      supplierName,
      supplier: supplierId,
    };
  }

  return { relatedInvoices: ref, paymentReason: 'facture_fournisseur' };
}

export function buildArchivePickerList(documents, typeKeys, resolveTitle) {
  const allowed = new Set(
    (Array.isArray(typeKeys) ? typeKeys : [typeKeys]).flatMap((k) => [
      k,
      String(k).replace(/-/g, '_'),
      String(k).replace(/_/g, '-'),
    ]),
  );
  return (Array.isArray(documents) ? documents : [])
    .filter((doc) => allowed.has(canonicalArchiveDocTypeKey(doc.type)))
    .filter((doc) => doc?.number && String(doc.number).trim())
    .map((doc) => {
      const reference = String(doc.number).trim();
      const title = resolveTitle ? resolveTitle(doc) : String(doc.title || '').trim();
      const dateStr = doc.date ? new Date(doc.date).toLocaleDateString(getBrandLocale()) : '';
      const label =
        title && title !== reference
          ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
          : dateStr
            ? `${reference} (${dateStr})`
            : reference;
      return { reference, label, date: doc.date, doc };
    })
    .sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    });
}

export function buildSupplierCreditNotePickerList(documents, resolveTitle) {
  return (Array.isArray(documents) ? documents : [])
    .filter(
      (doc) =>
        canonicalArchiveDocTypeKey(doc.type) === 'credit_note' &&
        isSupplierCreditNoteDocument(doc),
    )
    .filter((doc) => doc?.number && String(doc.number).trim())
    .map((doc) => {
      const reference = String(doc.number).trim();
      const title =
        (resolveTitle ? resolveTitle(doc) : '') ||
        resolveCreditNoteDisplayLabel(doc) ||
        reference;
      const dateStr = doc.date ? new Date(doc.date).toLocaleDateString(getBrandLocale()) : '';
      const label =
        title && title !== reference
          ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
          : dateStr
            ? `${reference} (${dateStr})`
            : reference;
      return { reference, label, date: doc.date, doc };
    })
    .sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    });
}

function archiveDocClientFields(doc) {
  const clientName = String(
    doc.clientName || doc.client_name || doc.client || '',
  ).trim();
  const clientId = String(
    doc.clientId || doc.client_id || '',
  ).trim();
  return { clientName, clientId };
}

/** Reçu de paiement : préremplissage depuis une facture archivée. */
export function buildPaymentReceiptPatchFromInvoice(doc) {
  if (!doc || canonicalArchiveDocTypeKey(doc.type) !== 'invoice') return {};
  const invRef = String(doc.number || '').trim();
  const total = archiveDocTotalAmount(doc);
  const { clientName, clientId } = archiveDocClientFields(doc);
  const desc = paymentLineDescription('invoice_collection', invRef);
  return {
    paymentInvoiceNumbers: invRef,
    clientName,
    client: clientId,
    paymentLines: [{
      lineType: 'invoice',
      reference: invRef,
      description: desc,
      amount: total || 0,
      relatedInvoice: invRef,
    }],
  };
}

/** Ligne vide pour Détails du paiement (Reçu de paiement). */
export function emptyPaymentReceiptLine(lineType = 'invoice') {
  return {
    lineType: lineType === 'credit_note' ? 'credit_note' : 'invoice',
    reference: '',
    description: '',
    amount: 0,
    relatedInvoice: '',
  };
}

export function buildPaymentReceiptLineFromInvoice(doc) {
  if (!doc) return emptyPaymentReceiptLine('invoice');
  const patch = buildPaymentReceiptPatchFromInvoice(doc);
  const line = patch.paymentLines?.[0] || {};
  return {
    lineType: 'invoice',
    reference: patch.paymentInvoiceNumbers || '',
    description: line.description || '',
    amount: Number(line.amount) || 0,
    relatedInvoice: patch.paymentInvoiceNumbers || '',
  };
}

/** Reçu de paiement : préremplissage depuis un Avoir sur vente archivé. */
export function buildPaymentReceiptPatchFromClientCreditNote(doc) {
  if (!doc || canonicalArchiveDocTypeKey(doc.type) !== 'credit_note') return {};
  if (!isClientCreditNoteDocument(doc)) return {};
  const cnRef = String(doc.number || '').trim();
  const invRef = String(
    doc.originalInvoiceNumber ||
      doc.original_invoice_number ||
      (doc.originalInvoice && doc.originalInvoice.number) ||
      '',
  ).trim();
  const total = archiveDocTotalAmount(doc);
  const { clientName, clientId } = archiveDocClientFields(doc);
  const desc = paymentLineDescription('credit_note_client', cnRef);
  return {
    clientCreditNoteRef: cnRef,
    paymentInvoiceNumbers: invRef,
    clientName,
    client: clientId,
    paymentLines: [{
      lineType: 'credit_note',
      reference: cnRef,
      description: desc,
      amount: total || 0,
      relatedInvoice: invRef,
    }],
  };
}

export function buildPaymentReceiptLineFromClientCreditNote(doc) {
  if (!doc) return emptyPaymentReceiptLine('credit_note');
  const patch = buildPaymentReceiptPatchFromClientCreditNote(doc);
  const line = patch.paymentLines?.[0] || {};
  return {
    lineType: 'credit_note',
    reference: patch.clientCreditNoteRef || '',
    description: line.description || '',
    amount: Number(line.amount) || 0,
    relatedInvoice: patch.paymentInvoiceNumbers || '',
  };
}

/** Agrège les refs d'en-tête depuis les lignes de paiement RP. */
export function syncPaymentReceiptHeaderFromLines(lines) {
  const arr = Array.isArray(lines) ? lines : [];
  const invRefs = [];
  const cnRefs = [];

  arr.forEach((line) => {
    if (!line) return;
    const ref = String(line.reference || '').trim();
    if (!ref) return;
    if (line.lineType === 'credit_note') {
      cnRefs.push(ref);
      const inv = String(line.relatedInvoice || '').trim();
      if (inv) invRefs.push(inv);
    } else {
      invRefs.push(ref);
    }
  });

  const unique = (list) => [...new Set(list.filter(Boolean))];
  return {
    paymentInvoiceNumbers: unique(invRefs).join(', '),
    clientCreditNoteRef: unique(cnRefs).join(', '),
  };
}

export function normalizePaymentReceiptLines(rows, headerRefs = {}) {
  const arr = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (!arr.length) {
    const inv = String(headerRefs.paymentInvoiceNumbers || '').trim();
    const cn = String(headerRefs.clientCreditNoteRef || '').trim();
    if (inv) {
      return [{
        lineType: 'invoice',
        reference: inv.split(',')[0].trim(),
        description: paymentLineDescription('invoice_collection', inv.split(',')[0].trim()),
        amount: 0,
        relatedInvoice: inv.split(',')[0].trim(),
      }];
    }
    if (cn) {
      return [{
        lineType: 'credit_note',
        reference: cn,
        description: paymentLineDescription('credit_note_client', cn),
        amount: 0,
        relatedInvoice: '',
      }];
    }
    return [emptyPaymentReceiptLine('invoice')];
  }

  return arr.map((row) => {
    if (row.lineType === 'invoice' || row.lineType === 'credit_note') {
      return {
        lineType: row.lineType,
        reference: String(row.reference || '').trim(),
        description: String(row.description || '').trim(),
        amount: Number(row.amount) || 0,
        relatedInvoice: String(row.relatedInvoice || '').trim(),
      };
    }
    const desc = String(row.description || '').trim();
    const lower = desc.toLowerCase();
    if (lower.includes('avoir')) {
      return {
        lineType: 'credit_note',
        reference: String(headerRefs.clientCreditNoteRef || '').trim(),
        description: desc,
        amount: Number(row.amount) || 0,
        relatedInvoice: String(headerRefs.paymentInvoiceNumbers || '').trim(),
      };
    }
    return {
      lineType: 'invoice',
      reference: String(headerRefs.paymentInvoiceNumbers || '').trim().split(',')[0] || '',
      description: desc || paymentLineDescription('invoice_collection'),
      amount: Number(row.amount) || 0,
      relatedInvoice: String(headerRefs.paymentInvoiceNumbers || '').trim().split(',')[0] || '',
    };
  });
}

export function getUsedPaymentReceiptLineRefs(lines, excludeIndex, lineType) {
  const type = lineType === 'credit_note' ? 'credit_note' : 'invoice';
  return new Set(
    (Array.isArray(lines) ? lines : [])
      .map((line, i) => {
        if (i === excludeIndex) return '';
        const lt = line?.lineType === 'credit_note' ? 'credit_note' : 'invoice';
        if (lt !== type) return '';
        return String(line?.reference || '').trim();
      })
      .filter(Boolean),
  );
}

export function buildClientCreditNotePickerList(documents, resolveTitle) {
  return (Array.isArray(documents) ? documents : [])
    .filter(
      (doc) =>
        canonicalArchiveDocTypeKey(doc.type) === 'credit_note' &&
        isClientCreditNoteDocument(doc),
    )
    .filter((doc) => doc?.number && String(doc.number).trim())
    .map((doc) => {
      const reference = String(doc.number).trim();
      const title =
        (resolveTitle ? resolveTitle(doc) : '') ||
        resolveCreditNoteDisplayLabel(doc) ||
        reference;
      const dateStr = doc.date ? new Date(doc.date).toLocaleDateString(getBrandLocale()) : '';
      const label =
        title && title !== reference
          ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
          : dateStr
            ? `${reference} (${dateStr})`
            : reference;
      return { reference, label, date: doc.date, doc };
    })
    .sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    });
}

export function buildClientInvoicePickerList(documents, resolveTitle) {
  return buildArchivePickerList(documents, ['invoice'], resolveTitle);
}

export function matchPaymentReceiptClientId(patch, clientOptions) {
  if (!patch || !Array.isArray(clientOptions)) return patch;
  if (patch.client) return patch;
  const name = String(patch.clientName || '').trim().toLowerCase();
  if (!name) return patch;
  const hit = clientOptions.find(
    (c) =>
      String(c.name || c.label || c.value || '')
        .trim()
        .toLowerCase() === name,
  );
  if (hit?.id != null) return { ...patch, client: String(hit.id) };
  return patch;
}

export function buildSupplierInvoiceRefPickerList(documents) {
  const seen = new Set();
  const rows = [];
  const add = (ref, sourceLabel) => {
    const r = String(ref ?? '').trim();
    if (!r || seen.has(r.toLowerCase())) return;
    seen.add(r.toLowerCase());
    rows.push({
      reference: r,
      label: sourceLabel ? `${r} — ${sourceLabel}` : r,
    });
  };
  (Array.isArray(documents) ? documents : []).forEach((doc) => {
    const t = canonicalArchiveDocTypeKey(doc.type);
    if (t === 'return_slip') {
      add(
        doc.returnOriginalInvoice || doc.return_original_invoice,
        i18n.t('document_types.return_slip'),
      );
    }
    if (t === 'credit_note' && isSupplierCreditNoteDocument(doc)) {
      add(
        doc.originalInvoiceNumber || doc.original_invoice_number,
        getCreditNoteLabelSupplier(),
      );
    }
  });
  return rows.sort((a, b) => a.reference.localeCompare(b.reference, 'fr'));
}

/** Associe supplierName au id fournisseur connu (liste déroulante). */
export function matchPaymentVoucherSupplierId(patch, supplierOptions) {
  if (!patch || !Array.isArray(supplierOptions)) return patch;
  if (patch.supplier) return patch;
  const name = String(patch.supplierName || '').trim().toLowerCase();
  if (!name) return patch;
  const hit = supplierOptions.find(
    (s) =>
      String(s.name || s.label || s.value || '')
        .trim()
        .toLowerCase() === name,
  );
  if (hit?.id != null) return { ...patch, supplier: String(hit.id) };
  return patch;
}

export function resolveCreditNoteDisplayLabel(doc) {
  if (!doc) return null;
  const kn = String(doc.type || '').replace(/-/g, '_');
  if (kn !== 'credit_note') return null;
  return isSupplierCreditNoteDocument(doc)
    ? getCreditNoteLabelSupplier()
    : getCreditNoteLabelClient();
}

export function resolveCreditNoteDisplayColor(doc) {
  return isSupplierCreditNoteDocument(doc) ? '#60a5fa' : '#10b981';
}

export function resolveArchiveDocumentTypeLabel(document, translateDocumentType) {
  const creditLabel = resolveCreditNoteDisplayLabel(document);
  if (creditLabel) return creditLabel;
  const displayKey = resolvePaymentDocumentDisplayType(document) ?? document?.type;
  return translateDocumentType(displayKey);
}

/**
 * Date facture liée à l'avoir — uniquement si saisie dans originalInvoice.date.
 * Évite le repli sur originalInvoiceDate (souvent prérempli à aujourd'hui alors que le champ est vide).
 */
export function resolveExplicitOriginalInvoiceDate(formData) {
  const f = formData || {};
  const inv = f.originalInvoice;
  if (inv && typeof inv === 'object' && Object.prototype.hasOwnProperty.call(inv, 'date')) {
    return inv.date != null ? String(inv.date).trim() : '';
  }
  const flat = f.originalInvoiceDate ?? f.original_invoice_date;
  return flat != null ? String(flat).trim() : '';
}

/** PDF / aperçu : AVOIR SUR VENTE ou AVOIR SUR ACHAT selon le partenaire. */
export function resolveCreditNotePdfTitles(formData = {}, documentType, partnerType) {
  const kn = String(documentType || formData?.type || '').replace(/-/g, '_');
  if (kn !== 'credit_note') return null;
  const doc = {
    type: 'credit_note',
    ...formData,
    number: formData?.number ?? formData?.documentNumber,
  };
  if (partnerType === 'supplier' && !isSupplierCreditNoteDocument(doc)) {
    doc.creditNoteParty = 'supplier';
    doc.isSupplier = true;
  }
  const label = resolveCreditNoteDisplayLabel(doc) || getCreditNoteLabelClient();
  return { titleUpper: label.toUpperCase(), titleFr: label.toLowerCase() };
}

/**
 * Maps document type codes to their translation keys
 */
/** Bon de versement : comptes émetteur/récepteur sans colonnes dédiées — encodés dans payer_contact */
const BV_PAYER_CONTACT_PREFIX = 'BV_ACC:';

export function encodePaymentVoucherAccountsPayerContact(issuerAccount, receiverAccount) {
  const i = String(issuerAccount ?? '').trim();
  const r = String(receiverAccount ?? '').trim();
  if (!i && !r) return '';
  return `${BV_PAYER_CONTACT_PREFIX}${encodeURIComponent(i)}|${encodeURIComponent(r)}`;
}

export function decodePaymentVoucherAccountsPayerContact(payerContact) {
  const s = String(payerContact ?? '');
  if (!s.startsWith(BV_PAYER_CONTACT_PREFIX))
    return { issuerAccount: '', receiverAccount: '' };
  const rest = s.slice(BV_PAYER_CONTACT_PREFIX.length);
  const idx = rest.indexOf('|');
  if (idx < 0) return { issuerAccount: '', receiverAccount: '' };
  try {
    return {
      issuerAccount: decodeURIComponent(rest.slice(0, idx)),
      receiverAccount: decodeURIComponent(rest.slice(idx + 1)),
    };
  } catch {
    return { issuerAccount: '', receiverAccount: '' };
  }
}

export const documentTypeMap = {
  'invoice': 'document_types.invoice',
  'quote': 'document_types.quote',
  'proforma': 'document_types.proforma',
  'quote_request': 'document_types.quote_request',
  'quote-request': 'document_types.quote_request',
  'proforma_request': 'document_types.proforma_request',
  'proforma-request': 'document_types.proforma_request',
  'delivery_note': 'document_types.delivery_note',
  'delivery-note': 'document_types.delivery_note',
  'delivery': 'document_types.delivery',
  'material_exit': 'document_types.material_exit',
  'material-exit': 'document_types.material_exit',
  'purchase_order': 'document_types.purchase_order',
  'purchase-order': 'document_types.purchase_order',
  'shipping': 'document_types.shipping',
  'shipping-slip': 'document_types.shipping_slip',
  'shipping_slip': 'document_types.shipping_slip',
  'return-slip': 'document_types.return_slip',
  'return_slip': 'document_types.return_slip',
  'reception-note': 'document_types.reception_note',
  'reception_note': 'document_types.reception_note',
  'client-return-slip': 'document_types.client_return_slip',
  'client_return_slip': 'document_types.client_return_slip',
  'credit-note': 'document_types.credit_note',
  'credit_note': 'document_types.credit_note',
  'payment-voucher': 'document_types.payment_voucher',
  'payment_voucher': 'document_types.payment_voucher',
  'payment-slip': 'document_types.payment_voucher',
  'payment_slip': 'document_types.payment_voucher',
  'payment-receipt': 'document_types.payment_receipt',
  'payment_receipt': 'document_types.payment_receipt',
  'exit-slip': 'document_types.exit_slip',
  'exit_slip': 'document_types.exit_slip',
  'transfer-slip': 'document_types.transfer_slip',
  'transfer_slip': 'document_types.transfer_slip'
};

/**
 * N° client / fournisseur pour en-tête PDF (Clients → client_number, Fournisseurs → supplier_number).
 * @param {object} [partner]
 * @param {{ isSupplier?: boolean, formData?: object }} [options]
 */
export function resolvePartnerReferenceCode(partner, options = {}) {
  const { isSupplier = false, formData } = options;
  if (partner && typeof partner === 'object') {
    if (isSupplier) {
      const fromPartner = String(
        partner.supplier_number ||
          partner.supplierNumber ||
          partner.code ||
          '',
      ).trim();
      if (fromPartner) return fromPartner;
    } else {
      const fromPartner = String(
        partner.client_number || partner.clientNumber || partner.clientCode || '',
      ).trim();
      if (fromPartner) return fromPartner;
    }
  }
  const fromForm = String(formData?.partnerNumber || formData?.partner_number || '').trim();
  return fromForm;
}

/** Ex. FOURNISSEUR — FRN-M008 (sans code si absent). */
export function buildDocumentPartnerSectionLabel(baseLabel, partner, options = {}) {
  const label = String(baseLabel || '').trim();
  if (!label) return '';
  const code = resolvePartnerReferenceCode(partner, options);
  return code ? `${label} — ${code}` : label;
}

/** Bon de réception — libellés et calcul valeur HT indicative (aperçu / PDF). */
export const RECEPTION_INDICATIVE_HT_LABEL =
  'قيمة استلامية للبضائع (بدون ض.ق.م):';

export const RECEPTION_INDICATIVE_HT_BOX_LABEL = 'قيمة استلامية (بدون ض.ق.م):';

export function isReceptionIndicativeTotalsRow(row) {
  return row?.id === 'valeur_ht';
}

export function isReceptionNoteDocumentType(documentType) {
  const dt = String(documentType ?? '')
    .trim()
    .replace(/_/g, '-')
    .toLowerCase();
  return dt === 'reception-note' || dt === 'receipt-note';
}

export function sumReceptionIndicativeHT(formData) {
  const items = formData?.items || [];
  return items.reduce((sum, item) => {
    const lineTotal = parseFloat(item.total);
    if (!Number.isNaN(lineTotal) && lineTotal > 0) return sum + lineTotal;
    const qty =
      parseFloat(item.quantityReceived ?? item.quantity_received) ||
      parseFloat(item.quantityOrdered) ||
      parseFloat(item.quantity) ||
      0;
    const price = parseFloat(item.price ?? item.unitPrice ?? item.unit_price) || 0;
    if (qty > 0 && price > 0) return sum + qty * price;
    return sum;
  }, 0);
}

export function previewItemHasLineContent(item) {
  if (!item || typeof item !== 'object') return false;
  return [item.product_name, item.description, item.designation, item.name, item.product].some(
    (v) => v != null && String(v).trim() !== '',
  );
}

export function normalizeReceptionLineItem(item) {
  const src = item && typeof item === 'object' ? item : {};
  const qtyOrdered =
    parseFloat(src.quantityOrdered) ||
    parseFloat(src.quantity) ||
    parseFloat(src.qty) ||
    0;
  const qtyReceived =
    parseFloat(src.quantityReceived ?? src.quantity_received) ||
    parseFloat(src.quantityDelivered) ||
    qtyOrdered;
  const price =
    parseFloat(src.price ?? src.unitPrice ?? src.unit_price) || 0;
  const lineTotal =
    parseFloat(src.total) ||
    ((qtyReceived > 0 ? qtyReceived : qtyOrdered) > 0 && price > 0
      ? (qtyReceived > 0 ? qtyReceived : qtyOrdered) * price
      : 0);
  const productName =
    src.product_name ||
    src.name ||
    src.designation ||
    src.description ||
    (typeof src.product === 'string' ? src.product : '') ||
    '';

  return {
    ...src,
    reference: src.reference || src.productRef || src.product_ref || src.code || '',
    designation: src.designation || productName,
    product_name: productName,
    name: src.name || productName,
    description: src.description || src.name || src.designation || productName,
    quantity: qtyReceived > 0 ? qtyReceived : qtyOrdered,
    quantityOrdered: qtyOrdered,
    quantityReceived: qtyReceived,
    price,
    unit_price: price,
    unitPrice: price,
    total: lineTotal,
    unit: src.unit || 'u',
    condition: src.condition || 'conforme',
    defects: src.defects || '',
    location: src.location || '',
  };
}

export default {
  useDocumentTypeTranslator,
  documentTypeMap,
  resolvePartnerReferenceCode,
  buildDocumentPartnerSectionLabel,
  resolvePaymentDocumentDisplayType,
  isSupplierCreditNoteDocument,
  isClientCreditNoteDocument,
  getCreditNoteLabelSupplier,
  getCreditNoteLabelClient,
  resolveCreditNoteDisplayLabel,
  resolveCreditNoteDisplayColor,
  resolveArchiveDocumentTypeLabel,
  resolveCreditNotePdfTitles,
  resolveExplicitOriginalInvoiceDate,
  CREDIT_NOTE_LABEL_SUPPLIER,
  CREDIT_NOTE_LABEL_CLIENT,
  getPaymentVoucherReasonOptions,
  PAYMENT_VOUCHER_REASON_OPTIONS,
  formatPaymentVoucherReason,
  resolvePaymentVoucherReasonValue,
  buildPaymentDocumentLineDescriptions,
  summarizePaymentDocumentTitleCore,
  canonicalArchiveDocTypeKey,
  findArchiveDocumentByNumber,
  buildPaymentVoucherPatchFromPurchaseOrder,
  buildPaymentVoucherPatchFromCreditNote,
  buildPaymentVoucherPatchFromSupplierInvoice,
  emptyPaymentVoucherLine,
  buildPaymentVoucherLineFromPurchaseOrder,
  buildPaymentVoucherLineFromCreditNote,
  syncPaymentVoucherHeaderFromLines,
  normalizePaymentVoucherLines,
  getUsedPaymentVoucherLineRefs,
  buildSupplierCreditNotePickerList,
  buildSupplierInvoiceRefPickerList,
  matchPaymentVoucherSupplierId,
  buildPaymentReceiptPatchFromInvoice,
  buildPaymentReceiptPatchFromClientCreditNote,
  emptyPaymentReceiptLine,
  buildPaymentReceiptLineFromInvoice,
  buildPaymentReceiptLineFromClientCreditNote,
  syncPaymentReceiptHeaderFromLines,
  normalizePaymentReceiptLines,
  getUsedPaymentReceiptLineRefs,
  buildClientCreditNotePickerList,
  buildClientInvoicePickerList,
  matchPaymentReceiptClientId,
  resolvePartnerReferenceCode,
  buildDocumentPartnerSectionLabel,
  RECEPTION_INDICATIVE_HT_LABEL,
  RECEPTION_INDICATIVE_HT_BOX_LABEL,
  isReceptionIndicativeTotalsRow,
  isReceptionNoteDocumentType,
  sumReceptionIndicativeHT,
  previewItemHasLineContent,
  normalizeReceptionLineItem,
};
